import { GridSortModel } from '@mui/x-data-grid/models';
import {
  IApiSupersetChartDataRequest,
  IApiSupersetQuery,
  IApiSupersetQueryFilter,
  IApiSupersetFormData,
  IApiSupersetDataSource,
  IApiSupersetQueryContext,
  IApiSupersetMetric,
  IApiSupersetSqlColumn,
  SupersetDatasourceString,
} from '@cbridge-packages/api-superset';

export interface ISearchableColumnForFilter {
  /** Column name (or label for virtual columns) */
  columnName: string;
  /** SQL expression (for virtual columns) */
  expression?: string;
}

export interface IBuildChartDataRequestParams {
  /** Datasource object with id and type */
  datasource: IApiSupersetDataSource;
  /** Form data from chart metadata */
  formData: IApiSupersetFormData;
  /** Pagination offset */
  offset?: number;
  /** Pagination limit (page size) */
  limit?: number;
  /** MUI DataGrid sort model */
  sortModel?: GridSortModel;
  /** MUI DataGrid filter model */
  userFilters?: IApiSupersetQueryFilter[];
  /** Include row count query */
  includeRowCount?: boolean;
  /** Include total row count query (WITHOUT user filters and quick filter) */
  includeTotalRowCount?: boolean;
  /** Quick filter search values (from MUI DataGrid quickFilterValues) */
  quickFilterValues?: string[];
  /** Searchable columns with expressions (supports virtual columns and GROUP BY awareness) */
  searchableColumns?: ISearchableColumnForFilter[];
  /** Additional programmatic filters (e.g. order_id filter for embedded grids) */
  additionalFilters?: IApiSupersetQueryFilter[];
}

/** Unique-ish id columns worth appending as sort tiebreakers when the report shows them */
const TIEBREAKER_ID_COLUMNS = ['id', 'uuid', 'order_id', 'execution_id', 'collector_execution_id'];

/**
 * Appends deterministic tiebreaker columns to the sort order. ORDER BY on a
 * non-unique column leaves the order of ties undefined, and ClickHouse returns
 * them in a different order per query — so paginated chunks overlap or miss
 * rows, and the grid shows duplicated/blank rows while the export comes back
 * in yet another order (LB-2854).
 */
const appendOrderByTiebreakers = (
  orderby: NonNullable<IApiSupersetQuery['orderby']>,
  formData: IApiSupersetFormData,
): IApiSupersetQuery['orderby'] => {
  const usedKeys = new Set(orderby.map(([col]) => (typeof col === 'string' ? col : col.label)));

  const tiebreakers: [string, boolean][] = [];
  const addTiebreaker = (col: string, isAsc: boolean) => {
    if (!usedKeys.has(col)) {
      usedKeys.add(col);
      tiebreakers.push([col, isAsc]);
    }
  };

  if (formData.query_mode === 'raw') {
    // Superset treats an adhoc (SQL) column in ORDER BY as a metric and groups the query by every
    // selected column, so a hidden chart order column cannot be a tiebreaker then (LB-5493)
    const groupedBySelection = orderby.some(([col]) => typeof col !== 'string');
    const selectedColumns = new Set(formData.all_columns.filter((col): col is string => typeof col === 'string'));
    // Chart-configured default order columns (guaranteed valid for the dataset)
    formData.order_by_cols?.forEach((raw) => {
      try {
        const [col, isAsc] = JSON.parse(raw);
        if (typeof col === 'string' && (!groupedBySelection || selectedColumns.has(col))) {
          addTiebreaker(col, Boolean(isAsc));
        }
      } catch {
        // Ignore parsing errors
      }
    });
    // Id columns shown by the report
    formData.all_columns.forEach((col) => {
      if (typeof col === 'string' && TIEBREAKER_ID_COLUMNS.includes(col)) {
        addTiebreaker(col, true);
      }
    });
  } else {
    // Aggregate mode: the groupby set is the unique key of every result row
    formData.groupby?.forEach((col) => addTiebreaker(col, true));
  }

  return [...orderby, ...tiebreakers];
};

/**
 * Escapes a user value for a ClickHouse string literal. A backslash starts an escape sequence
 * there, so a raw one could end the literal early (`\\'`) and inject SQL
 */
const escapeSqlString = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "''");

/**
 * Renders a filter value as a SQL literal: NULL, a number, an ('a', 'b') list or a quoted string
 */
const formatSqlValue = (value: IApiSupersetQueryFilter['val']): string => {
  if (value === null) {
    return 'NULL';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  const quote = (v: string | number | boolean) => (typeof v === 'number' ? String(v) : `'${escapeSqlString(String(v))}'`);
  if (Array.isArray(value)) {
    // For IN operator: ('value1', 'value2')
    return `(${value.map(quote).join(', ')})`;
  }
  return quote(value);
};

/**
 * Escapes special characters in SQL LIKE pattern
 */
const escapeSqlLikeValue = (value: string): string => {
  return escapeSqlString(value).replace(/%/g, '\\%').replace(/_/g, '\\_');
};

/**
 * Builds WHERE clause for quick filter search with OR conditions
 * Uses ILIKE for case-insensitive search across multiple columns
 * Supports both regular columns and virtual columns with SQL expressions
 */
const buildQuickFilterWhere = (
  quickFilterValues: string[],
  searchableColumns: ISearchableColumnForFilter[],
): string => {
  if (!quickFilterValues?.length || !searchableColumns?.length) {
    return '';
  }

  // Join all search terms into a single search string
  const searchTerm = quickFilterValues.join(' ').trim();
  if (!searchTerm) {
    return '';
  }

  const escapedValue = escapeSqlLikeValue(searchTerm);

  // Build OR conditions for each searchable column
  const conditions = searchableColumns.map((col) => {
    // For virtual columns with expression, use the SQL expression
    if (col.expression) {
      return `(${col.expression}) ILIKE '%${escapedValue}%'`;
    }

    // For regular columns, use column name
    return `${col.columnName} ILIKE '%${escapedValue}%'`;
  });

  return `(${conditions.join(' OR ')})`;
};

/**
 * Builds a Superset chart data request from DataGrid parameters
 */
export const buildChartDataRequest = ({
  datasource,
  formData,
  offset = 0,
  limit = 20,
  sortModel,
  userFilters = [],
  includeRowCount = true,
  includeTotalRowCount = false,
  quickFilterValues,
  searchableColumns,
  additionalFilters = [],
}: IBuildChartDataRequestParams): IApiSupersetChartDataRequest => {
  // Build base query from form_data
  const baseQuery: IApiSupersetQuery = {
    columns: formData.query_mode === 'raw'
      ? formData.all_columns
      : formData.groupby,
    metrics: formData.query_mode === 'aggregate'
      ? formData.metrics
      : undefined,
    row_limit: limit,
    row_offset: offset,
    applied_time_extras: {},
  };

  // Build map of metric labels to metrics for HAVING clause construction
  const metricMap = new Map<string, IApiSupersetMetric>();
  if (formData.query_mode === 'aggregate' && formData.metrics) {
    formData.metrics.forEach((metric) => {
      metricMap.set(metric.label, metric);
    });
  }

  // Build map of SQL column labels to their definitions for sorting
  const sqlColumnMap = new Map<string, IApiSupersetSqlColumn>();
  if (formData.query_mode === 'raw') {
    formData.all_columns.forEach((col) => {
      if (typeof col !== 'string') {
        sqlColumnMap.set(col.label, col);
      }
    });
  }

  // Separate user filters into WHERE (columns), HAVING (metrics), and raw SQL WHERE conditions
  const whereFilters: IApiSupersetQueryFilter[] = [];
  const havingClauses: string[] = [];
  const rawWhereConditions: string[] = [];

  userFilters.forEach((filter) => {
    // Adhoc (SQL) columns are addressed by their expression in raw SQL conditions
    const sqlColumn = sqlColumnMap.get(filter.col);
    const columnSql = sqlColumn ? `(${sqlColumn.sqlExpression})` : filter.col;

    // Superset API doesn't support NOT ILIKE as a filter operator,
    // so convert these to raw SQL WHERE conditions
    if (filter.op === 'not ilike') {
      rawWhereConditions.push(`${columnSql} NOT ILIKE ${formatSqlValue(filter.val)}`);
      return;
    }

    // IS NULL / IS NOT NULL: also check for empty strings
    // ClickHouse stores empty values as '' (not NULL), so we need to check both
    if (filter.op === 'IS NULL') {
      rawWhereConditions.push(`(${columnSql} IS NULL OR ${columnSql} = '')`);
      return;
    }
    if (filter.op === 'IS NOT NULL') {
      rawWhereConditions.push(`(${columnSql} IS NOT NULL AND ${columnSql} != '')`);
      return;
    }

    const metric = metricMap.get(filter.col);
    if (metric?.sqlExpression) {
      // This is a metric - build HAVING clause: metric.sqlExpression operator value
      havingClauses.push(`${metric.sqlExpression} ${filter.op} ${formatSqlValue(filter.val)}`);
    } else if (sqlColumn) {
      // Superset silently drops a `filters` entry whose column is not a dataset column,
      // so an adhoc column is filtered through the raw WHERE on its expression (LB-5493)
      rawWhereConditions.push(`${columnSql} ${filter.op} ${formatSqlValue(filter.val)}`);
    } else {
      // This is a regular column - use WHERE clause
      whereFilters.push(filter);
    }
  });

  // Add filters from adhoc_filters (default chart filters)
  const defaultFilters: IApiSupersetQueryFilter[] = formData.adhoc_filters
    ?.filter((f) => f.expressionType === 'SIMPLE' && f.operator !== 'TEMPORAL_RANGE')
    .map((f) => ({
      col: f.subject,
      op: f.operator,
      val: f.comparator,
    })) || [];

  baseQuery.filters = [...defaultFilters, ...additionalFilters, ...whereFilters];

  // Add HAVING clause if there are metric filters
  if (havingClauses.length > 0) {
    baseQuery.extras = {
      ...baseQuery.extras,
      having: havingClauses.join(' AND '),
    };
  }

  // Build WHERE clause from the raw SQL conditions and the quick filter
  const whereConditions: string[] = [...rawWhereConditions];

  if (quickFilterValues?.length && searchableColumns?.length) {
    const quickFilterWhere = buildQuickFilterWhere(quickFilterValues, searchableColumns);
    if (quickFilterWhere) {
      whereConditions.push(quickFilterWhere);
    }
  }

  if (whereConditions.length > 0) {
    baseQuery.extras = {
      ...baseQuery.extras,
      where: whereConditions.join(' AND '),
    };
  }

  // Add sorting
  if (sortModel?.length) {
    baseQuery.orderby = sortModel.map((sort) => [
      metricMap.get(sort.field) ?? sqlColumnMap.get(sort.field) ?? sort.field,
      sort.sort === 'asc',
    ]);
  } else if (formData.order_by_cols?.length) {
    // Use default sorting from form_data
    try {
      baseQuery.orderby = formData.order_by_cols.map((col) => {
        const parsed = JSON.parse(col);
        return [parsed[0], parsed[1]];
      });
    } catch {
      // Ignore parsing errors
    }
  }

  if (baseQuery.orderby?.length) {
    baseQuery.orderby = appendOrderByTiebreakers(baseQuery.orderby, formData);
  }

  const queries: IApiSupersetQuery[] = [baseQuery];

  // Add rowcount query if needed
  if (includeRowCount) {
    queries.push({
      ...baseQuery,
      row_limit: 0,
      row_offset: 0,
      is_rowcount: true,
    });
  }

  // Add total rowcount query (WITHOUT user filters and quick filter)
  if (includeTotalRowCount) {
    queries.push({
      ...baseQuery,
      filters: [...defaultFilters, ...additionalFilters], // adhoc_filters + additional, WITHOUT userFilters
      extras: {},                                         // Remove quick filter WHERE clause
      orderby: undefined,                                 // Remove sorting (not needed for rowcount)
      is_rowcount: true,
      row_limit: 0,
      row_offset: 0,
    });
  }

  return {
    datasource,
    queries,
    result_format: 'json',
    result_type: 'results',
    form_data: formData,
  };
};

/**
 * Parses query_context JSON string from chart metadata
 */
export const parseQueryContext = (queryContextJson: string): IApiSupersetQueryContext | null => {
  try {
    return JSON.parse(queryContextJson);
  } catch {
    return null;
  }
};

/**
 * Validates and returns datasource string (e.g., "3__table")
 * Returns the string as-is if valid, null otherwise
 */
export const validateDatasourceString = (datasourceStr: string): SupersetDatasourceString | null => {
  const match = datasourceStr.match(/^(\d+)__(\w+)$/);
  if (!match) {
    return null;
  }

  return datasourceStr as SupersetDatasourceString;
};

/**
 * Extracts datasource info from form_data.datasource string (e.g., "3__table")
 * @deprecated Use validateDatasourceString instead for API requests
 */
export const parseDatasourceString = (datasourceStr: string): IApiSupersetDataSource | null => {
  const match = datasourceStr.match(/^(\d+)__(\w+)$/);
  if (!match) {
    return null;
  }

  return {
    id: parseInt(match[1], 10),
    type: match[2] as 'table',
  };
};

/**
 * Converts IApiSupersetDataSource object to datasource string
 */
export const datasourceToString = (datasource: IApiSupersetDataSource): SupersetDatasourceString => {
  return `${datasource.id}__${datasource.type}` as SupersetDatasourceString;
};
